import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, History, Camera, Download, Clock, User, Globe, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/config';

const t = (key, lang) => {
  const dict = {
    title: { ar: 'سجل تعديلات الأسئلة', sv: 'Frågeredigeringshistorik', en: 'Question Edit History' },
    subtitle: {
      ar: 'كل تعديل تقوم به على أي سؤال يُسجَّل هنا تلقائياً',
      sv: 'Varje redigering du gör på en fråga loggas här automatiskt',
      en: 'Every edit you make to a question is logged here automatically',
    },
    refresh: { ar: 'تحديث', sv: 'Uppdatera', en: 'Refresh' },
    snapshot: { ar: 'إنشاء نسخة احتياطية', sv: 'Skapa snapshot', en: 'Create Snapshot' },
    snapshotDone: { ar: 'تم إنشاء النسخة الاحتياطية', sv: 'Snapshot skapad', en: 'Snapshot created' },
    snapshots: { ar: 'النسخ الاحتياطية', sv: 'Snapshots', en: 'Snapshots' },
    noHistory: { ar: 'لا توجد تعديلات حتى الآن.', sv: 'Inga redigeringar ännu.', en: 'No edits yet.' },
    when: { ar: 'الوقت', sv: 'Tid', en: 'When' },
    user: { ar: 'بواسطة', sv: 'Av', en: 'By' },
    questionId: { ar: 'رقم السؤال', sv: 'Fråge-ID', en: 'Question ID' },
    questionText: { ar: 'النص (سويدي)', sv: 'Text (svenska)', en: 'Text (Swedish)' },
    sourceLang: { ar: 'لغة الإدخال', sv: 'Källspråk', en: 'Source lang' },
    fileName: { ar: 'اسم الملف', sv: 'Filnamn', en: 'Filename' },
    size: { ar: 'الحجم', sv: 'Storlek', en: 'Size' },
    date: { ar: 'التاريخ', sv: 'Datum', en: 'Date' },
    type: { ar: 'النوع', sv: 'Typ', en: 'Type' },
    manual: { ar: 'يدوي', sv: 'Manuell', en: 'Manual' },
    auto: { ar: 'تلقائي', sv: 'Auto', en: 'Auto' },
    snapshotting: { ar: 'جارٍ إنشاء النسخة...', sv: 'Skapar snapshot...', en: 'Creating snapshot...' },
  };
  return dict[key]?.[lang] || dict[key]?.en || key;
};

const QuestionEditHistory = ({ token, i18n }) => {
  const lang = i18n?.language || 'en';
  const [history, setHistory] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snapshotting, setSnapshotting] = useState(false);

  const fetchHistory = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [histRes, snapRes] = await Promise.all([
        axios.get(`${API}/admin/questions/edit-history?limit=200`, { headers }),
        axios.get(`${API}/admin/questions/snapshots`, { headers }),
      ]);
      setHistory(histRes.data?.history || []);
      setSnapshots(snapRes.data?.snapshots || []);
    } catch (err) {
      console.error(err);
      toast.error(lang === 'ar' ? 'خطأ في جلب السجل' : 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [token, lang]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleSnapshot = async () => {
    if (snapshotting || !token) return;
    setSnapshotting(true);
    try {
      const res = await axios.post(`${API}/admin/questions/manual-snapshot`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${t('snapshotDone', lang)} (${res.data.questions_count} ${lang === 'ar' ? 'سؤال' : 'qs'} • ${res.data.size_mb} MB)`);
      fetchHistory();
    } catch (err) {
      console.error(err);
      toast.error(lang === 'ar' ? 'فشل إنشاء النسخة الاحتياطية' : 'Snapshot failed');
    } finally {
      setSnapshotting(false);
    }
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '-';
    try {
      const d = new Date(isoStr);
      return d.toLocaleString(lang === 'ar' ? 'ar-EG' : 'sv-SE', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return isoStr.slice(0, 19); }
  };

  return (
    <div className="space-y-6" data-testid="question-edit-history">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <History className="w-6 h-6 text-emerald-600" />
            {t('title', lang)}
          </h2>
          <p className="text-sm text-slate-500 mt-1">{t('subtitle', lang)}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchHistory}
            disabled={loading}
            data-testid="qhistory-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="ml-2">{t('refresh', lang)}</span>
          </Button>
          <Button
            onClick={handleSnapshot}
            disabled={snapshotting}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="qhistory-snapshot-btn"
          >
            {snapshotting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            <span className="ml-2">{snapshotting ? t('snapshotting', lang) : t('snapshot', lang)}</span>
          </Button>
        </div>
      </div>

      {/* Edit history table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-500" />
            {t('title', lang)} <span className="text-sm text-slate-400 font-normal">({history.length})</span>
          </h3>
        </div>
        {loading ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
          </div>
        ) : history.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <History className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>{t('noHistory', lang)}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="qhistory-table">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-start px-4 py-3 font-semibold">{t('when', lang)}</th>
                  <th className="text-start px-4 py-3 font-semibold">{t('user', lang)}</th>
                  <th className="text-start px-4 py-3 font-semibold">{t('questionId', lang)}</th>
                  <th className="text-start px-4 py-3 font-semibold">{t('questionText', lang)}</th>
                  <th className="text-start px-4 py-3 font-semibold">{t('sourceLang', lang)}</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatTime(h.timestamp)}</td>
                    <td className="px-4 py-3 text-slate-700">
                      <span className="inline-flex items-center gap-1">
                        <User className="w-3 h-3 text-slate-400" />
                        {h.user_email || h.user_name || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{(h.question_id || '').slice(0, 18)}…</td>
                    <td className="px-4 py-3 text-slate-700 max-w-md truncate" title={h.question_text_sv}>
                      {h.question_text_sv || '-'}
                    </td>
                    <td className="px-4 py-3">
                      {h.source_language && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 text-xs uppercase">
                          <Globe className="w-3 h-3" />
                          {h.source_language}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Snapshots list */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Camera className="w-4 h-4 text-slate-500" />
            {t('snapshots', lang)} <span className="text-sm text-slate-400 font-normal">({snapshots.length})</span>
          </h3>
        </div>
        {snapshots.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">—</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="qhistory-snapshots-table">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-start px-4 py-3 font-semibold">{t('fileName', lang)}</th>
                  <th className="text-start px-4 py-3 font-semibold">{t('type', lang)}</th>
                  <th className="text-start px-4 py-3 font-semibold">{t('size', lang)}</th>
                  <th className="text-start px-4 py-3 font-semibold">{t('date', lang)}</th>
                </tr>
              </thead>
              <tbody>
                {snapshots.slice(0, 30).map((s, i) => (
                  <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-xs text-slate-700 break-all">{s.filename}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                        s.is_manual ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {s.is_manual ? t('manual', lang) : t('auto', lang)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{s.size_mb} MB</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatTime(s.modified_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionEditHistory;
